<script setup lang="ts">
interface Props {
  title: string;
  onBack?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  // Esconde o "Voltar". Existe pra quando a tela NÃO é a página: dentro de um
  // modal (ex.: a Loja aberta pelo Clube Fidelidade) não há pra onde voltar — o
  // default do botão é `router.back()`, que ali navegaria o app por baixo do
  // modal. Sem a prop, nada muda.
  hideBack?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  onBack: undefined,
  onRefresh: undefined,
  isRefreshing: false,
  headingTag: 'h2',
  hideBack: false,
});

const router = useRouter();

function handleBack() {
  if (props.onBack) {
    props.onBack();
  } else {
    router.back();
  }
}
</script>

<template>
  <header
    class="screen-header"
    :draggable="false"
  >
    <div class="flex justify-between items-center w-full h-9">
      <div class="flex items-center">
        <button
          v-if="!hideBack"
          aria-label="Voltar"
          class="screen-header-back-button"
          @click="handleBack"
        >
          <NuxtIcon
            aria-hidden="true"
            name="arrows/arrow_left"
          />
        </button>
        <component
          :is="headingTag"
          class="screen-header-title"
          >{{ title }}</component
        >
      </div>

      <div class="flex items-center gap-1">
        <slot name="right">
          <button
            v-if="onRefresh"
            aria-label="Atualizar"
            class="screen-header-action-button"
            @click="onRefresh"
          >
            <NuxtIcon
              aria-hidden="true"
              :class="[
                'screen-header-sync-icon',
                { 'screen-header-animate': isRefreshing },
              ]"
              name="sync"
            />
          </button>
        </slot>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
.screen-header {
  @apply mt-2 mb-2 lg:flex items-center lg:justify-start;
}

.screen-header-back-button {
  @apply flex items-center bg-transparent text-texts/60;

  svg {
    @apply flex-none;
  }
}

.screen-header-title {
  @apply inline ml-3 text-[15px] text-texts font-bold select-none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.screen-header-action-button {
  @apply p-2 bg-transparent text-white/60 hover:text-white transition-colors cursor-pointer flex items-center justify-center;

  svg {
    @apply w-5 h-5;
  }

  @media (max-width: 767px) {
    @apply bg-[#010612] rounded-md px-2 py-1.5 h-auto min-h-[32px];
    outline: 0.59px solid rgba(38, 38, 38, 1);
    outline-offset: -0.59px;
    backdrop-filter: blur(5.85px);
  }
}

.screen-header-sync-icon {
  svg {
    @apply w-4 h-4;
  }

  @media (max-width: 767px) {
    svg {
      @apply w-3.5 h-3.5;
    }
  }
}

.screen-header-animate {
  animation: rotate 600ms infinite linear;
}

@keyframes rotate {
  to {
    transform: rotate(1turn);
  }
}
</style>
